import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  SquaresFour, FileText, Receipt, SignOut, ClipboardText, CurrencyDollar, Warning, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LOGO_URL, FLYER_URL } from "../lib/assets";
import QuotesPanel from "../components/admin/QuotesPanel";
import InvoicesPanel from "../components/admin/InvoicesPanel";

const money = (v) => `${Number(v || 0).toFixed(2)} $`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/admin");
  }, [user, navigate]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const doLogout = async () => {
    await logout();
    toast.success("Déconnecté");
    navigate("/admin");
  };

  const onConvert = (data) => {
    setPrefill(data);
    setTab("invoices");
  };

  const nav = [
    { id: "dashboard", label: "Tableau de bord", icon: SquaresFour },
    { id: "quotes", label: "Devis", icon: FileText },
    { id: "invoices", label: "Factures", icon: Receipt },
  ];

  if (!user || user === false) {
    return <div className="min-h-screen grid place-items-center bg-sage text-forest">Chargement…</div>;
  }

  const kpis = stats ? [
    { label: "Devis totaux", value: stats.total_quotes, icon: ClipboardText, color: "text-forest bg-forest/10" },
    { label: "Nouveaux devis", value: stats.new_quotes, icon: Sparkle, color: "text-gold-dark bg-gold/15" },
    { label: "Factures", value: stats.total_invoices, icon: Receipt, color: "text-forest bg-forest/10" },
    { label: "Impayées", value: stats.unpaid_invoices, icon: Warning, color: "text-destructive bg-destructive/10" },
    { label: "Revenus encaissés", value: money(stats.revenue), icon: CurrencyDollar, color: "text-leaf bg-leaf/15" },
  ] : [];

  return (
    <div className="min-h-screen bg-sage flex" data-testid="admin-dashboard">
      {/* sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-forest-deep text-white p-5">
        <div className="flex items-center gap-3 px-2">
          <img src={LOGO_URL} alt="Kelsy" className="h-11 w-11 rounded-xl object-cover" />
          <div><div className="font-serif text-lg leading-none">Kelsy</div><div className="text-[10px] uppercase tracking-[0.25em] text-leaf">Admin</div></div>
        </div>
        <nav className="mt-10 flex flex-col gap-1">
          {nav.map((n) => {
            const Icon = n.icon;
            const active = tab === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setTab(n.id)}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${active ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                data-testid={`sidebar-${n.id}`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} /> {n.label}
              </button>
            );
          })}
        </nav>
        <button onClick={doLogout} className="mt-auto flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5 transition-colors" data-testid="logout-btn">
          <SignOut size={20} /> Déconnexion
        </button>
      </aside>

      {/* main */}
      <main className="flex-1 min-w-0">
        {/* top bar mobile */}
        <div className="md:hidden flex items-center justify-between bg-forest-deep text-white px-5 py-4">
          <div className="flex items-center gap-2"><img src={LOGO_URL} alt="Kelsy" className="h-9 w-9 rounded-lg object-cover" /><span className="font-serif text-lg">Kelsy Admin</span></div>
          <button onClick={doLogout} data-testid="logout-btn-mobile"><SignOut size={22} /></button>
        </div>
        <div className="md:hidden flex gap-2 px-4 py-3 overflow-x-auto bg-forest-deep/95">
          {nav.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold ${tab === n.id ? "bg-white text-forest" : "bg-white/10 text-white"}`} data-testid={`tab-mobile-${n.id}`}>
              {n.label}
            </button>
          ))}
        </div>

        <div className="p-5 sm:p-8 max-w-6xl mx-auto">
          {tab === "dashboard" && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              {/* welcome banner with flyer */}
              <div className="relative overflow-hidden rounded-3xl bg-forest text-white">
                <img src={FLYER_URL} alt="Kelsy Housecleaning" className="absolute right-0 top-0 h-full w-1/2 object-cover object-top opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/95 to-transparent" />
                <div className="relative p-8 sm:p-10 max-w-md">
                  <div className="text-xs font-bold uppercase tracking-[0.3em] text-gold">Espace administrateur</div>
                  <h1 className="mt-3 font-serif text-4xl">Bienvenue, {user.name?.split(" ")[0] || "Admin"}</h1>
                  <p className="mt-3 text-white/75">Gérez vos demandes de devis et vos factures en un seul endroit.</p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
                {kpis.map((k, i) => {
                  const Icon = k.icon;
                  return (
                    <div key={i} className="rounded-2xl bg-white border border-forest/10 p-5" data-testid={`kpi-${i}`}>
                      <div className={`grid h-10 w-10 place-items-center rounded-xl ${k.color}`}><Icon size={20} weight="fill" /></div>
                      <div className="mt-4 text-2xl font-bold text-forest">{k.value}</div>
                      <div className="text-xs text-forest/55 mt-0.5">{k.label}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 grid lg:grid-cols-2 gap-4">
                <button onClick={() => setTab("quotes")} className="rounded-2xl bg-white border border-forest/10 p-6 text-left hover:border-forest/30 transition-colors" data-testid="goto-quotes">
                  <FileText size={28} weight="duotone" className="text-forest" />
                  <div className="mt-3 font-serif text-2xl text-forest">Gérer les devis</div>
                  <div className="text-sm text-forest/60">Préparez et envoyez des devis personnalisés.</div>
                </button>
                <button onClick={() => setTab("invoices")} className="rounded-2xl bg-white border border-forest/10 p-6 text-left hover:border-forest/30 transition-colors" data-testid="goto-invoices">
                  <Receipt size={28} weight="duotone" className="text-gold" />
                  <div className="mt-3 font-serif text-2xl text-forest">Gérer les factures</div>
                  <div className="text-sm text-forest/60">Créez, envoyez et suivez les paiements.</div>
                </button>
              </div>
            </motion.div>
          )}

          {tab === "quotes" && (
            <div>
              <h1 className="font-serif text-3xl text-forest mb-6">Devis</h1>
              <QuotesPanel onChanged={loadStats} onConvert={onConvert} />
            </div>
          )}

          {tab === "invoices" && (
            <div>
              <h1 className="font-serif text-3xl text-forest mb-6">Factures</h1>
              <InvoicesPanel prefill={prefill} clearPrefill={() => setPrefill(null)} onChanged={loadStats} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
